import { useEffect, useRef, useState } from "react";
import { getSession, needsRefresh, refreshSession } from "@web-shared/lib/account";
import { getSurface, roomSocketUrl } from "@web-shared/lib/api";
import { RoomSocketLink, type LinkStatus, type SessionProbe } from "@web-shared/lib/room-socket-link";
import type { JamParticipant, JamServerMessage } from "@web-shared/lib/types";

export interface LiveBatch {
  seq: number;
  /** When this client saw it — the DO stamps its own `at` only in the log. */
  receivedAt: string;
  payload: unknown;
}

/**
 * `failed` is a link that has stopped trying: the session is over, or the
 * socket would not open for several minutes. It tries again when the tab is
 * shown, focused or back online, or when the caller remounts it.
 */
export type SocketStatus = LinkStatus;

const MAX_LIVE_BATCHES = 300;

export interface RoomSocketOptions {
  /** Join the roster as a participant rather than a hidden observer. */
  edit?: boolean;
  /** Every accepted-op broadcast, in arrival order, for the replica to fold. */
  onBatch?: (payload: unknown) => void;
  /** A fresh connection. Anything missed while disconnected is a gap. */
  onOpen?: () => void;
}

/**
 * Before retrying a connection that never opened. A refused upgrade is
 * indistinguishable from a slow room in the browser, so when the access token
 * in the URL is due, refresh it: a new pair means the next attempt carries a
 * token the API will take; a refusal means the session is over and the link
 * should stop. A token that is not due is not the problem, and refreshing it
 * anyway would rotate a refresh token another tab may be about to use.
 */
async function probeSession(): Promise<SessionProbe> {
  if (getSurface() !== "account") return "unknown";
  const session = getSession();
  if (!session) return "unknown"; // anonymous listener: nothing to refresh
  if (!needsRefresh(session)) return "alive";
  if (await refreshSession()) return "alive";
  // A refresh that timed out keeps the session; one the API refused clears it.
  return getSession() ? "unknown" : "gone";
}

/**
 * Connection to one room. By default the worker opens it with ?observe=1, so
 * attaching never appears in the room's participant roster and never sends a
 * presence frame to the people actually jamming. `edit` drops that, because a
 * client that can change the Set should be visible to the people whose Set it is.
 *
 * Receive-only, like every other client of this DO: the room takes proposals
 * over HTTP, and the only frame we ever send is a keepalive ping.
 *
 * Reconnects with exponential backoff (lib/room-socket-link.ts), from the
 * close event only, and gives up when the session is gone or the socket keeps
 * refusing to open — a stale token must not retry every 3 s for a day (#124).
 */
export function useRoomSocket(
  jamId: string | undefined,
  options: RoomSocketOptions = {},
): {
  status: SocketStatus;
  participants: JamParticipant[] | null;
  batches: LiveBatch[];
  latestSeq: number | null;
} {
  const { edit = false } = options;
  // Held in a ref so a caller re-rendering with a new closure doesn't tear the
  // socket down — only jamId and edit-mode should ever cause a reconnect.
  const handlers = useRef(options);
  handlers.current = options;
  const [status, setStatus] = useState<SocketStatus>("connecting");
  const [participants, setParticipants] = useState<JamParticipant[] | null>(null);
  const [batches, setBatches] = useState<LiveBatch[]>([]);
  const [latestSeq, setLatestSeq] = useState<number | null>(null);

  useEffect(() => {
    if (!jamId) return;
    setBatches([]);
    setParticipants(null);

    const link = new RoomSocketLink({
      // Built per attempt: after a refresh the URL carries the new token.
      open: () => new WebSocket(roomSocketUrl(jamId, edit)),
      probe: probeSession,
      onStatus: setStatus,
      onOpen: () => handlers.current.onOpen?.(),
      onMessage: (data) => {
        let msg: JamServerMessage | { type: "pong" };
        try {
          msg = JSON.parse(String(data)) as JamServerMessage;
        } catch {
          return; // a frame we can't read is not worth tearing the socket down
        }
        if (msg.type === "welcome") {
          setParticipants(msg.participants);
          setLatestSeq(msg.latestSeq);
        } else if (msg.type === "presence") {
          setParticipants(msg.participants);
        } else if (msg.type === "batch") {
          setLatestSeq(msg.seq);
          handlers.current.onBatch?.(msg.payload);
          setBatches((prev) =>
            [{ seq: msg.seq, receivedAt: new Date().toISOString(), payload: msg.payload }, ...prev].slice(
              0,
              MAX_LIVE_BATCHES,
            ),
          );
        }
      },
    });
    link.start();

    // Signs that a retry may now succeed where the last run of them did not.
    const resume = () => link.resume();
    const shown = () => {
      if (document.visibilityState === "visible") link.resume();
    };
    window.addEventListener("online", resume);
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", shown);

    return () => {
      window.removeEventListener("online", resume);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", shown);
      link.stop();
    };
  }, [jamId, edit]);

  return { status, participants, batches, latestSeq };
}
